/**
 * ProjectAuditTab — Phase 17
 *
 * A full audit / activity log for a project record.
 * In Live mode it fetches from GET /api/projects/{id}/audit-log.
 * In Demo/Tour mode it falls back to client-side mock data
 * (completion-% audit entries from Phase 12, fixed-fee invoice records from
 * Phases 3-4, and extra events passed via props).
 *
 * PRD reference: Section 11
 * Task reference: 17-FE.2, 17-FE.3, 17-FE.4
 */

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { getApiBaseUrl, getDataMode } from "@/lib/data-service";
import { authFetch } from "@/lib/auth-fetch";
import {
  Clock,
  Download,
  Filter,
  ClipboardList,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import type { CompletionPercentAuditEntry } from "@/types";

// ─── Types ───────────────────────────────────────────────────────────────────

export type AuditEventCategory =
  | "completion"
  | "status"
  | "financial"
  | "billing"
  | "invoice"
  | "budget"
  | "client";

export interface AuditEvent {
  id: string;
  category: AuditEventCategory;
  changedAt: string; // ISO datetime
  changedBy: string; // display name or "System"
  description: string; // pre-formatted human-readable summary
  metadata?: Record<string, unknown>;
}

/** Minimal invoice shape — matches the fixedFeeInvoices entries in project-details.tsx */
export interface AuditInvoice {
  id: number;
  invoiceNumber: string | null;
  date: string | null;       // invoice date / milestone due date
  dueDate?: string | null;
  description?: string | null;
  amount: number;
  status: string;
  milestoneId?: number;
  createdDate?: string;
  sentDate?: string;
  paidDate?: string;
}

export interface ProjectAuditTabProps {
  /** Numeric project ID — used for fetching from the backend in Live mode */
  projectId?: number;
  /** Project code used in CSV filename, e.g. "KPMG-FSI-002" */
  projectCode: string;
  /** Project type — controls which data sources are shown */
  projectType: string;
  /** Billing trigger type — needed to gate completion events */
  billingTriggerType?: string;
  /** Completion-% audit entries from Phase 12 mock data */
  completionAuditEntries?: CompletionPercentAuditEntry[];
  /** Fixed-fee invoice records from Phase 3-4 mock data */
  fixedFeeInvoices?: AuditInvoice[];
  /** Pre-selected category filter — used when navigating from KPI card link */
  initialCategoryFilter?: AuditEventCategory | "all";
  /** Additional pre-built audit events (e.g. client change events from project-details) */
  extraEvents?: AuditEvent[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format an ISO datetime string as "15 Jul 2025" */
function formatAuditDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Format an ISO datetime string as "09:14" */
function formatAuditTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Format an ISO date string as "15 Jul 2025" (date only) */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Format a currency amount as "£90,000" */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Build a human-readable description string for a CompletionPercentAuditEntry */
function buildCompletionDescription(entry: CompletionPercentAuditEntry): string {
  switch (entry.changeType) {
    case "auto-calculation":
      if (entry.approvedHoursAtChange !== undefined && entry.budgetHoursAtChange !== undefined) {
        return `Auto-calculated: ${entry.previousValue}% → ${entry.newValue}% (based on ${entry.approvedHoursAtChange.toLocaleString()}h of ${entry.budgetHoursAtChange.toLocaleString()}h approved)`;
      }
      return `Auto-calculated: ${entry.previousValue}% → ${entry.newValue}%`;
    case "manual-override":
      return `Manual override: ${entry.previousValue}% → ${entry.newValue}% · set by ${entry.changedByName}`;
    case "override-cleared":
      return `Override cleared: reverted to auto-calculated value (${entry.newValue}%)`;
    default:
      return `Completion updated: ${entry.previousValue}% → ${entry.newValue}%`;
  }
}

/** Derive AuditEvents from CompletionPercentAuditEntry array */
function completionEntriesToEvents(entries: CompletionPercentAuditEntry[]): AuditEvent[] {
  return entries.map((entry) => ({
    id: `completion-${entry.id}`,
    category: "completion" as AuditEventCategory,
    changedAt: entry.changedAt,
    changedBy: entry.changeType === "auto-calculation" ? "System" : entry.changedByName,
    description: buildCompletionDescription(entry),
    metadata: {
      previousValue: entry.previousValue,
      newValue: entry.newValue,
      changeType: entry.changeType,
    },
  }));
}

/** Derive AuditEvents from fixed-fee invoice records.
 *  Each invoice can generate up to three events: created, sent, paid.
 */
function invoicesToEvents(invoices: AuditInvoice[]): AuditEvent[] {
  const events: AuditEvent[] = [];

  for (const inv of invoices) {
    const num = inv.invoiceNumber ?? `#${inv.id}`;
    const milestoneName = inv.description
      ? inv.description.replace(/^Invoice for:\s*/i, "")
      : "milestone";
    const amount = formatCurrency(inv.amount);

    // Created event — use createdDate if available, fall back to invoice date
    const createdDate = inv.createdDate ?? inv.date;
    if (createdDate) {
      events.push({
        id: `invoice-created-${inv.id}`,
        category: "invoice",
        changedAt: createdDate.includes("T") ? createdDate : `${createdDate}T09:00:00Z`,
        changedBy: "System",
        description: `Invoice ${num} generated for ${milestoneName} · ${amount}`,
        metadata: { invoiceId: inv.id, invoiceNumber: num, type: "created" },
      });
    }

    // Sent event
    if (inv.sentDate) {
      const dueLabel = inv.dueDate ? ` · due ${formatDate(inv.dueDate)}` : "";
      events.push({
        id: `invoice-sent-${inv.id}`,
        category: "invoice",
        changedAt: inv.sentDate.includes("T") ? inv.sentDate : `${inv.sentDate}T09:00:00Z`,
        changedBy: "System",
        description: `Invoice ${num} marked as sent${dueLabel}`,
        metadata: { invoiceId: inv.id, invoiceNumber: num, type: "sent" },
      });
    }

    // Paid event
    if (inv.paidDate) {
      events.push({
        id: `invoice-paid-${inv.id}`,
        category: "invoice",
        changedAt: inv.paidDate.includes("T") ? inv.paidDate : `${inv.paidDate}T09:00:00Z`,
        changedBy: "System",
        description: `Invoice ${num} marked as paid · ${amount}`,
        metadata: { invoiceId: inv.id, invoiceNumber: num, type: "paid" },
      });
    }
  }

  return events;
}

// ─── Category Badge ───────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<AuditEventCategory, string> = {
  completion: "Completion",
  status: "Status",
  financial: "Financial",
  billing: "Billing",
  invoice: "Invoice",
  budget: "Budget",
  client: "Client",
};

function CategoryBadge({ category }: { category: AuditEventCategory }) {
  switch (category) {
    case "completion":
      return <Badge variant="solid" accent="teal" className="text-xs">{CATEGORY_LABELS.completion}</Badge>;
    case "invoice":
      return <Badge variant="solid" accent="success" className="text-xs">{CATEGORY_LABELS.invoice}</Badge>;
    case "status":
      return <Badge variant="solid" accent="info" className="text-xs">{CATEGORY_LABELS.status}</Badge>;
    case "budget":
      return <Badge variant="solid" accent="orange" className="text-xs">{CATEGORY_LABELS.budget}</Badge>;
    case "financial":
      return <Badge variant="solid" accent="warning" className="text-xs">{CATEGORY_LABELS.financial}</Badge>;
    case "billing":
      return <Badge variant="solid" accent="purple" className="text-xs">{CATEGORY_LABELS.billing}</Badge>;
    case "client":
      return (
        <Badge variant="solid" accent="violet" className="text-xs gap-1">
          <Users className="h-3 w-3" />
          {CATEGORY_LABELS.client}
        </Badge>
      );
    default:
      return <Badge variant="secondary" className="text-xs">{category}</Badge>;
  }
}

// ─── CSV Export ───────────────────────────────────────────────────────────────

function exportToCsv(events: AuditEvent[], projectCode: string) {
  const header = ["Date", "Time", "User", "Category", "Description"];
  const rows = events.map((e) => [
    formatAuditDate(e.changedAt),
    formatAuditTime(e.changedAt),
    e.changedBy,
    CATEGORY_LABELS[e.category] ?? e.category,
    e.description,
  ]);

  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const today = new Date().toISOString().slice(0, 10);
  const filename = `audit-${projectCode}-${today}.csv`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ─── Date Picker ─────────────────────────────────────────────────────────────

function DateFilterPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Date | undefined;
  onChange: (d: Date | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs font-normal min-w-[110px] justify-start">
          {value
            ? value.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
            : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => {
            onChange(d);
            setOpen(false);
          }}
          captionLayout="dropdown"
          startMonth={new Date(1900, 0)}
          endMonth={new Date(2100, 11)}
        />
        {value && (
          <div className="p-2 border-t">
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { onChange(undefined); setOpen(false); }}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const PAGE_SIZE = 50;
const DEMO_PAGE_SIZE = 25;

export function ProjectAuditTab({
  projectId,
  projectCode,
  projectType,
  billingTriggerType,
  completionAuditEntries = [],
  fixedFeeInvoices = [],
  initialCategoryFilter = "all",
  extraEvents = [],
}: ProjectAuditTabProps) {
  // ── Live mode state ─────────────────────────────────────────────────────────
  const isLive = getDataMode() === "live";
  const [liveEvents, setLiveEvents] = useState<AuditEvent[]>([]);
  const [liveLoading, setLiveLoading] = useState(isLive && !!projectId);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [livePage, setLivePage] = useState(1);
  const [liveTotal, setLiveTotal] = useState(0);

  // Handle mode transitions — reset live state when toggling demo ↔ live
  const prevIsLiveRef = useRef(isLive);
  if (prevIsLiveRef.current !== isLive) {
    prevIsLiveRef.current = isLive;
    if (isLive) {
      setLiveLoading(!!projectId);
      setLiveError(null);
      setLivePage(1);
      setLiveTotal(0);
    } else {
      setLiveEvents([]);
      setLiveLoading(false);
      setLiveError(null);
      setLivePage(1);
      setLiveTotal(0);
    }
  }

  /** Map a backend ProjectAuditLog row to the frontend AuditEvent shape */
  const mapBackendEvent = useCallback((e: Record<string, unknown>): AuditEvent => {
    const raw = (e.category as string ?? "").toLowerCase();
    // Map backend categories to the frontend union; fall back to "status"
    const categoryMap: Record<string, AuditEventCategory> = {
      completion: "completion",
      status: "status",
      financial: "financial",
      billing: "billing",
      invoice: "invoice",
      budget: "budget",
      client: "client",
    };
    return {
      id: String(e.id),
      category: categoryMap[raw] ?? "status",
      changedAt: (e.changedAt as string) ?? new Date().toISOString(),
      changedBy: (e.changedBy as string) ?? "System",
      description: (e.description as string) ?? "",
      metadata: e.metadata ? (typeof e.metadata === "string" ? JSON.parse(e.metadata as string) : e.metadata as Record<string, unknown>) : undefined,
    };
  }, []);

  // ── Filter state (declared before fetchLiveEvents so filters can be passed as server-side params) ──
  const [categoryFilter, setCategoryFilter] = useState<string>(initialCategoryFilter);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [visibleCount, setVisibleCount] = useState(DEMO_PAGE_SIZE);

  /** Fetch audit events from the backend API with server-side pagination and filtering */
  const fetchLiveEvents = useCallback(async () => {
    if (!isLive || !projectId) return;
    setLiveLoading(true);
    setLiveError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', String(livePage));
      params.append('pageSize', String(PAGE_SIZE));
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (dateFrom) params.append('from', dateFrom.toISOString());
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        params.append('to', to.toISOString());
      }
      const url = `${getApiBaseUrl()}/projects/${projectId}/audit-log?${params.toString()}`;
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch project audit log: ${response.status}`);
      }
      const data = await response.json();
      const events: AuditEvent[] = (data.events ?? []).map(mapBackendEvent);
      setLiveEvents(events);
      setLiveTotal(data.total ?? events.length);
    } catch (err) {
      console.error("[ProjectAuditTab] Error fetching audit log:", err);
      setLiveError(err instanceof Error ? err.message : "Failed to load audit history");
      setLiveEvents([]);
      setLiveTotal(0);
    } finally {
      setLiveLoading(false);
    }
  }, [isLive, projectId, livePage, categoryFilter, dateFrom, dateTo, mapBackendEvent]);

  useEffect(() => {
    fetchLiveEvents();
  }, [fetchLiveEvents]);

  // ── Build the unified event list ────────────────────────────────────────────
  const allEvents = useMemo<AuditEvent[]>(() => {
    // In Live mode, use API events (merged with any extra client-side events)
    if (isLive) {
      const events = [...liveEvents, ...extraEvents];
      return events.sort(
        (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
      );
    }

    // Demo/Tour mode — build from client-side props
    const events: AuditEvent[] = [];

    // Completion % events — only for Fixed Fee % Complete projects
    if (
      projectType === "fixed-price" &&
      billingTriggerType === "percent-complete" &&
      completionAuditEntries.length > 0
    ) {
      events.push(...completionEntriesToEvents(completionAuditEntries));
    }

    // Invoice events — only for Fixed Fee projects
    if (projectType === "fixed-price" && fixedFeeInvoices.length > 0) {
      events.push(...invoicesToEvents(fixedFeeInvoices));
    }

    // Extra pre-built events (e.g. client change events passed from project-details)
    if (extraEvents.length > 0) {
      events.push(...extraEvents);
    }

    // Sort newest-first
    return events.sort(
      (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
    );
  }, [isLive, liveEvents, projectType, billingTriggerType, completionAuditEntries, fixedFeeInvoices, extraEvents]);

  // ── Derive available categories ──────────────────────────────────────────────
  // In live mode show all categories (server-side filtering may return 0 for some).
  // In demo mode only show categories that have events in the current dataset.
  const availableCategories = useMemo<AuditEventCategory[]>(() => {
    if (isLive) {
      return ["completion", "status", "financial", "billing", "invoice", "budget", "client"] as AuditEventCategory[];
    }
    const seen = new Set<AuditEventCategory>();
    allEvents.forEach((e) => seen.add(e.category));
    return Array.from(seen);
  }, [isLive, allEvents]);

  // ── Apply filters ───────────────────────────────────────────────────────────
  // In live mode, filters are applied server-side — skip client-side filtering
  const filteredEvents = useMemo(() => {
    if (isLive) return allEvents;
    return allEvents.filter((e) => {
      if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
      const ts = new Date(e.changedAt);
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (ts < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (ts > to) return false;
      }
      return true;
    });
  }, [isLive, allEvents, categoryFilter, dateFrom, dateTo]);

  const visibleEvents = isLive ? filteredEvents : filteredEvents.slice(0, visibleCount);
  const hasMore = isLive ? false : filteredEvents.length > visibleCount;
  const liveTotalPages = Math.max(1, Math.ceil(liveTotal / PAGE_SIZE));
  const filtersActive = categoryFilter !== "all" || !!dateFrom || !!dateTo;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Audit History</h2>
          <p className="text-sm text-muted-foreground">
            A chronological record of significant changes to this project
          </p>
        </div>
        {filteredEvents.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCsv(filteredEvents, projectCode)}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground shrink-0" />

            {/* Category filter */}
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setLivePage(1); setVisibleCount(DEMO_PAGE_SIZE); }}>
              <SelectTrigger className="h-8 text-xs w-[160px]">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {availableCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {CATEGORY_LABELS[cat]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Separator orientation="vertical" className="h-6" />

            {/* Date range */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">From</span>
              <DateFilterPicker
                label="Start date"
                value={dateFrom}
                onChange={(d) => { setDateFrom(d); setLivePage(1); setVisibleCount(DEMO_PAGE_SIZE); }}
              />
              <span className="text-xs text-muted-foreground">to</span>
              <DateFilterPicker
                label="End date"
                value={dateTo}
                onChange={(d) => { setDateTo(d); setLivePage(1); setVisibleCount(DEMO_PAGE_SIZE); }}
              />
            </div>

            {/* Clear filters */}
            {filtersActive && (
              <>
                <Separator orientation="vertical" className="h-6" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-muted-foreground"
                  onClick={() => {
                    setCategoryFilter("all");
                    setDateFrom(undefined);
                    setDateTo(undefined);
                    setLivePage(1);
                    setVisibleCount(DEMO_PAGE_SIZE);
                  }}
                >
                  Clear filters
                </Button>
              </>
            )}

            {/* Result count */}
            <span className="ml-auto text-xs text-muted-foreground">
              {isLive
                ? `${liveTotal} event${liveTotal !== 1 ? "s" : ""}${liveTotalPages > 1 ? ` · Page ${livePage} of ${liveTotalPages}` : ""}`
                : filteredEvents.length === allEvents.length
                  ? `${allEvents.length} event${allEvents.length !== 1 ? "s" : ""}`
                  : `${filteredEvents.length} of ${allEvents.length} event${allEvents.length !== 1 ? "s" : ""}`}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Event table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Event Log
          </CardTitle>
          <CardDescription>Newest events first · All times in local timezone</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {liveLoading ? (
            /* Loading state — fetching from backend */
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <Clock className="h-10 w-10 text-muted-foreground/40 mb-3 animate-pulse" />
              <p className="text-sm font-medium text-muted-foreground">Loading audit history…</p>
            </div>
          ) : liveError ? (
            /* Error state — API call failed */
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <ClipboardList className="h-10 w-10 text-destructive/40 mb-3" />
              <p className="text-sm font-medium text-destructive">Failed to load audit history</p>
              <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs">{liveError}</p>
            </div>
          ) : allEvents.length === 0 ? (
            /* Empty state — no events at all */
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No audit history yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs">
                Changes to completion %, project status, and invoices will appear here as they happen.
              </p>
            </div>
          ) : filteredEvents.length === 0 ? (
            /* Empty state — filters active, no matches */
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <Filter className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No events match the current filters</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Try adjusting the category or date range.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader divided className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead className="w-[120px] bg-card select-none">Date</TableHead>
                    <TableHead className="w-[70px] bg-card select-none">Time</TableHead>
                    <TableHead className="w-[150px] bg-card select-none">User</TableHead>
                    <TableHead className="w-[120px] bg-card select-none">Category</TableHead>
                    <TableHead className="bg-card select-none">Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleEvents.map((event) => (
                    <TableRow key={event.id} className="even:bg-white odd:bg-neutral-50 odd:hover:bg-neutral-50 even:hover:bg-white">
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatAuditDate(event.changedAt)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap tabular-nums">
                        {formatAuditTime(event.changedAt)}
                      </TableCell>
                      <TableCell className="text-sm font-medium whitespace-nowrap">
                        {event.changedBy}
                      </TableCell>
                      <TableCell>
                        <CategoryBadge category={event.category} />
                      </TableCell>
                      <TableCell className="text-sm text-foreground">
                        {event.description}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination / Load more */}
              {isLive && liveTotalPages > 1 && (
                <div className="bg-card py-3 px-6 flex items-center justify-center gap-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={livePage <= 1}
                    onClick={() => setLivePage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    Page {livePage} of {liveTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={livePage >= liveTotalPages}
                    onClick={() => setLivePage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
              {hasMore && (
                <div className="bg-card py-3 px-6 flex justify-center border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setVisibleCount((c) => c + DEMO_PAGE_SIZE)}
                  >
                    Load more ({filteredEvents.length - visibleCount} remaining)
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
